import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../services/Interceptor";
import { likeAthlete } from "../services/AthleteService";
import { toggleFavouriteLocal } from "../store/slices/athleteSlice";

const EMPTY_FAVOURITES = [];

export default function useFavouriteAthletes() {
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const inFlightRef = useRef(new Set());

  const favouriteAthletes = useSelector(
    (state) => state.athletes.favouriteAthletes ?? EMPTY_FAVOURITES,
  );

  const [likeLoadingById, setLikeLoadingById] = useState({});

  const favouriteById = useMemo(
    () => new Map(favouriteAthletes.map((item) => [item.userId, item.liked])),
    [favouriteAthletes],
  );

  const getFavoriteState = useCallback(
    (athlete) => {
      if (!athlete?.userId) return false;
      return Boolean(
        favouriteById.get(athlete.userId) ?? athlete.liked ?? false,
      );
    },
    [favouriteById],
  );

  const handleFavoriteClick = useCallback(
    async (event, athlete, onError) => {
      event?.stopPropagation();

      const id = athlete?.userId;
      if (!id || inFlightRef.current.has(id)) return;

      inFlightRef.current.add(id);
      const nextValue = !getFavoriteState(athlete);

      setLikeLoadingById((prev) => ({ ...prev, [id]: true }));

      dispatch(toggleFavouriteLocal({ athleteUserId: id, liked: nextValue }));

      try {
        await likeAthlete(apiInstance, nextValue, id);
      } catch (error) {
        dispatch(
          toggleFavouriteLocal({ athleteUserId: id, liked: !nextValue }),
        );
        onError?.(error);
      } finally {
        inFlightRef.current.delete(id);
        setLikeLoadingById((prev) => {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        });
      }
    },
    [apiInstance, dispatch, getFavoriteState],
  );

  return {
    getFavoriteState,
    handleFavoriteClick,
    likeLoadingById,
  };
}
